export interface Project {
  id: number;
  slug: string;
  title: string;
  description: string;
  longDescription: string;
  tech: string[];
  category: string;
  liveUrl?: string;
  githubUrl?: string;
  image?: string;
  features: string[];
  challenges?: string;
  role: string;
  year: string;
}

export const projectsData: Project[] = [
  {
    id: 18,
    slug: 'inference-cluster',
    title: 'On-Premise AI Inference Cluster',
    description: 'Multi-node Apple M3 Ultra cluster running large open-source language models locally via a full Thunderbolt mesh. Built from bare metal — including patching the distributed inference framework to make it stable. Zero data egress, OpenAI-compatible API.',
    longDescription: 'A private multi-node AI inference cluster built entirely from bare metal. The build required deep expertise across Python, MLX, systems administration, peer-to-peer networking, and LLM inference infrastructure. The cluster uses tensor-parallel model sharding across nodes connected via Thunderbolt mesh, coordinated by the Exo framework. Achieves production-grade local AI performance at a fraction of cloud inference costs.',
    tech: ['Apple Silicon', 'Thunderbolt 4', 'MLX', 'exo', 'Python', 'macOS', 'Distributed Systems'],
    category: 'Infrastructure',
    image: '/inference-cluster-thumbnail.jpg',
    githubUrl: 'https://github.com/EddieJorden',
    features: [
      '5-node Apple Silicon cluster with full Thunderbolt mesh',
      'Thunderbolt full-mesh network topology',
      'Tensor-parallel model sharding across nodes',
      'Exo framework for distributed LLM inference',
      'Runs large open-source language models locally',
      'OpenAI-compatible API endpoint',
    ],
    challenges: 'Configuring low-latency peer-to-peer networking across 5 nodes, tuning tensor parallelism for optimal throughput, and debugging distributed inference failures at the systems level.',
    role: 'Systems Architect & Developer',
    year: '2026',
  },
  {
    id: 19,
    slug: 'custom-ai-agents',
    title: 'Custom AI Agent Setup',
    description: 'End-to-end AI agent solutions for businesses — from intelligent virtual assistants to automated workflow managers, custom-trained on your data and integrated with your existing systems.',
    longDescription: 'I help businesses unlock their potential with custom AI agents that transform how they operate. These intelligent systems are specifically designed and trained to handle tasks unique to your organization, from customer service automation to complex data analysis. By leveraging cutting-edge AI models and integrating seamlessly with your existing infrastructure, these agents provide 24/7 support, reduce operational costs, and deliver consistent, high-quality results. Whether you need a virtual assistant to handle customer inquiries, an automated workflow manager, or a specialized agent to process industry-specific data, I can create a solution tailored to your exact requirements.',
    tech: ['AI Integration', 'Custom LLMs', 'API Development', 'Cloud Infrastructure', 'Process Automation'],
    category: 'AI/Automation',
    image: '/custom-ai-agents-thumbnail.jpg',
    features: [
      '24/7 AI assistants custom-trained on your business data',
      'Multi-channel support — phone, chat, email, SMS',
      'CRM and business tool integration',
      'Automated workflows that scale with your team',
      'Scalable local or cloud AI infrastructure',
      'Seamless API integration with existing systems',
      'Real-time analytics and performance monitoring',
      'Ongoing optimization and support',
    ],
    challenges: 'Each business has unique processes and data. The challenge is building agents that truly understand the domain, integrate cleanly with existing tools, and deliver reliable results at scale — not just a chatbot wrapper.',
    role: 'AI Solutions Architect',
    year: '2026',
  },
  {
    id: 20,
    slug: 'ai-agent-orchestrator',
    title: 'AI Agent Orchestrator',
    description: 'A production multi-agent AI system that autonomously routes tasks across cloud and local models, spawns specialized sub-agents on demand, and runs 24/7 with built-in QC, cost routing, and persistent memory.',
    longDescription: 'A fully operational multi-agent AI orchestration system built on the OpenClaw platform. The system dynamically routes work between cloud models (for judgment and planning) and a locally-hosted M3 Ultra cluster (for code tasks). Specialized sub-agents are spawned on-demand for isolated tasks, with a quality-control review pass before any output reaches the user. Persistent memory across sessions, cross-channel messaging (Slack, Telegram, Signal), and a heartbeat system for proactive background monitoring.',
    tech: ['OpenClaw', 'Multi-Agent AI', 'M3 Ultra Cluster', 'TypeScript', 'Node.js', 'React', 'Distributed Inference'],
    category: 'AI/ML',
    image: '/ai-orchestrator-thumbnail.jpg',
    githubUrl: 'https://github.com/EddieJorden',
    features: [
      'Intelligent task routing between cloud and local LLMs',
      'On-demand sub-agent spawning for isolated workloads',
      'Built-in QC review pass before output reaches user',
      'Persistent cross-session memory (MEMORY.md + daily logs)',
      'Multi-channel presence: Slack, Telegram, Signal, Web',
      'Proactive heartbeat monitoring with background task execution',
      'On-premise M3 Ultra AI cluster at zero marginal inference cost',
    ],
    challenges: 'Building a reliable orchestration layer that can reason about when to use expensive cloud models vs. local inference, while maintaining consistent output quality and keeping the human always in the loop for important decisions.',
    role: 'Architect & Developer',
    year: '2026',
  },
  {
    id: 17,
    slug: 'preba',
    title: 'Preba — Premium Bathroom Access App',
    description: 'Startup landing page for Preba (Premium Bathroom), an app that gives users instant access to verified, rated public restrooms via business-hosted access codes.',
    longDescription: 'Preba is a startup landing page for a premium bathroom access app. Users get instant access to verified, rated public restrooms via business-hosted access codes. The site features dedicated sections for users, business owners, and investors — with pricing tiers, a trust/ratings system, live map mockup, and a business revenue dashboard. Built with React + Vite and deployed on Vercel.',
    tech: ['React', 'Vite', 'CSS Modules', 'React Router', 'Vercel'],
    category: 'Full-Stack',
    liveUrl: 'https://privy-landing.vercel.app',
    githubUrl: 'https://github.com/arlo-e-dev/preba-landing',
    image: '/preba-thumbnail.jpg',
    features: [
      'Multi-audience landing sections (Users, Business Owners, Investors)',
      'Pricing tier comparisons',
      'Trust and ratings system UI',
      'Live map mockup',
      'Business revenue dashboard preview',
      'Responsive design with CSS Modules',
    ],
    challenges: 'Designing a compelling pitch for three distinct audiences on a single page while keeping the UX intuitive and the messaging clear.',
    role: 'Full-Stack Developer',
    year: '2026',
  },
  {
    id: 16,
    slug: 'rateswipe',
    title: 'RateSwipe — Mortgage Marketplace',
    description: 'Tinder-style mortgage shopping app where buyers swipe through broker offers and brokers compete for leads.',
    longDescription: 'RateSwipe reimagines the mortgage shopping experience with a Tinder-style swipe UI. Buyers browse broker offers with a simple swipe interface, while brokers compete for leads through a subscription-gated dashboard. Features include a full buyer swipe UI, broker dashboard, subscription gate, match flow, and dark mode.',
    tech: ['Next.js', 'TypeScript', 'TailwindCSS', 'Framer Motion', 'Vercel'],
    category: 'Full-Stack',
    liveUrl: 'https://rateswipe.vercel.app',
    githubUrl: 'https://github.com/arlo-e-dev/rateswipe',
    image: '/rateswipe-thumbnail.jpg',
    features: [
      'Tinder-style swipe UI for mortgage offers',
      'Broker dashboard with subscription gating',
      'Match flow between buyers and brokers',
      'Dark mode support',
      'Responsive mobile-first design',
    ],
    challenges: 'Building an intuitive swipe-based UX for a traditionally complex financial product while maintaining trust and clarity.',
    role: 'Full-Stack Developer',
    year: '2026',
  },
  {
    id: 15,
    slug: 'swingby',
    title: 'SwingBy — Startup Landing Page',
    description: 'Investor-grade landing page for SwingBy, an AI-powered crowdsourced parcel pickup startup.',
    longDescription: 'SwingBy is an investor-grade landing page for an AI-powered crowdsourced parcel pickup startup. It features multi-audience sections for investors, drivers, and shippers — with real market data, dynamic pricing tiers, a full business model page with unit economics, and contextual sign-up flows. Built with React + Vite and deployed on Vercel.',
    tech: ['React', 'Vite', 'CSS Modules', 'React Router', 'Vercel'],
    category: 'Full-Stack',
    liveUrl: 'https://swingby-landing.vercel.app',
    githubUrl: 'https://github.com/arlo-e-dev/swingby-landing',
    image: '/swingby-thumbnail.jpg',
    features: [
      'Multi-audience sections (Investors, Drivers, Shippers)',
      'Real market data integration',
      'Dynamic pricing tiers',
      'Full business model with unit economics',
      'Contextual sign-up flows',
    ],
    challenges: 'Presenting complex logistics and financial data in a way that resonates with three very different audiences while maintaining a polished startup feel.',
    role: 'Full-Stack Developer',
    year: '2026',
  },
  {
    id: 1,
    slug: 'task-manager',
    title: 'Full Stack Task Manager',
    description: 'Complete task management application built with React, Redux, TypeScript, Node.js, Express, and MySQL. Features user authentication, task CRUD operations, and real-time updates.',
    longDescription: 'A production-grade task management application featuring full CRUD operations, user authentication, and real-time state management. The front end is built with React and Redux for predictable state, with TypeScript for type safety. The back end uses Node.js and Express with a MySQL database.',
    tech: ['React', 'Redux', 'TypeScript', 'Node.js', 'Express', 'MySQL'],
    category: 'Full-Stack',
    liveUrl: 'https://eddies-balance-app.netlify.app/',
    githubUrl: 'https://github.com/EddieJorden/balance-app',
    image: '/Task-app-thumbnail.png',
    features: [
      'User authentication and session management',
      'Full CRUD task operations',
      'Real-time state management with Redux',
      'RESTful API with Express',
      'MySQL database with migrations',
    ],
    role: 'Full-Stack Developer',
    year: '2023',
  },
  {
    id: 19,
    slug: 'ai-chatbot',
    title: 'Custom AI Chatbot',
    description: 'A custom AI chatbot capable of answering questions and producing natural language text responses.',
    longDescription: 'A custom-built AI chatbot that leverages language model capabilities to answer questions and generate text responses in real time. Built as a demonstration of integrating AI APIs into a React front end with a Node.js/Express backend.',
    tech: ['React', 'Node.js', 'AI/ML API', 'Express'],
    category: 'Full-Stack',
    liveUrl: 'https://eddies-balance-app.netlify.app/',
    githubUrl: 'https://github.com/EddieJorden/balance-app',
    image: '/AI-chatbot-thumbnail.png',
    features: [
      'Real-time AI-powered text generation',
      'Conversational question-answering interface',
      'Clean chat UI with message history',
      'Integration with language model APIs',
    ],
    challenges: 'Handling asynchronous AI API responses smoothly while maintaining a responsive and fluid chat experience.',
    role: 'Full-Stack Developer',
    year: '2024',
  },
  {
    id: 2,
    slug: 'simplespectra',
    title: 'SimpleSpectra.com',
    description: 'E-commerce platform currently in development. Built with modern web technologies to provide seamless shopping experience.',
    longDescription: 'A full-featured e-commerce platform with product catalog, shopping cart, and payment integration. Currently in active development with modern web technologies.',
    tech: ['React', 'Node.js', 'E-Commerce'],
    category: 'Full-Stack',
    liveUrl: 'https://simplespectra.com/',
    image: '/SimpleSpectra.com-thumbnail.png',
    features: [
      'Product catalog with search and filtering',
      'Shopping cart and checkout flow',
      'Responsive design for all devices',
    ],
    role: 'Full-Stack Developer',
    year: '2024',
  },
  {
    id: 3,
    slug: 'data-viz',
    title: 'Data Visualization Dashboard',
    description: 'Interactive data visualization component using D3.js. Built with React, Redux, and Express to display complex data in intuitive visual formats.',
    longDescription: 'An interactive data visualization dashboard that transforms complex datasets into clear visual narratives. Built with D3.js for rendering, React and Redux for state management, and Express for the API layer.',
    tech: ['React', 'Redux', 'D3.js', 'Express'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/ui-ux-project/',
    githubUrl: 'https://github.com/EddieJorden/ui-ux-project',
    image: '/Data-viz.png',
    features: [
      'Interactive D3.js visualizations',
      'Redux state management',
      'Express API backend',
      'Responsive chart layouts',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 4,
    slug: 'customer-directory',
    title: 'Optimized Customer Directory',
    description: 'High-performance React Redux application rendering 1000+ input fields. Optimized using React.memo and useCallback for maximum performance.',
    longDescription: 'A high-performance React Redux application that renders over 1,000 input fields while maintaining smooth interactions. Extensively optimized using React.memo, useCallback, and other performance techniques.',
    tech: ['React', 'Redux', 'Performance Optimization'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/ctw-project/',
    githubUrl: 'https://github.com/EddieJorden/ctw-project',
    image: '/Customer-directory.png',
    features: [
      'Renders 1,000+ input fields efficiently',
      'React.memo for component memoization',
      'useCallback for callback optimization',
      'Redux state management at scale',
    ],
    challenges: 'Maintaining responsive UI performance while rendering and managing state for over 1,000 interactive input fields simultaneously.',
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 5,
    slug: 'reddit-clone',
    title: 'Reddit Clone',
    description: 'Fully functional Reddit clone using the official Reddit API. Built with React and Redux for state management.',
    longDescription: 'A Reddit-style application consuming the official Reddit API, featuring subreddit browsing, post viewing, and real-time content. Built with React and Redux for clean, predictable state management.',
    tech: ['React', 'Redux', 'REST API'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/reddit-clone/',
    githubUrl: 'https://github.com/EddieJorden/reddit-clone',
    image: '/reddit_clone_thumbnail.png',
    features: [
      'Reddit API integration',
      'Subreddit browsing and filtering',
      'Post and comment viewing',
      'Redux state management',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 6,
    slug: 'flashcards',
    title: 'Flash Card Study App',
    description: 'Custom flashcard application for creating and studying topics with quiz functionality. Built with React and Redux.',
    longDescription: 'A custom flashcard quiz platform with topic management and interactive study modes. Users can create topics, add flashcards, and test their knowledge through interactive quizzes.',
    tech: ['React', 'Redux'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/flashcards/',
    githubUrl: 'https://github.com/EddieJorden/flashcards',
    image: '/flashcards-thumbnail.png',
    features: [
      'Topic creation and management',
      'Flashcard CRUD operations',
      'Interactive quiz mode',
      'Redux state management',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 7,
    slug: 'fotomatic',
    title: 'Fotomatic Responsive Site',
    description: 'Pixel-perfect responsive website built to exact designer specifications using modern CSS flexbox techniques.',
    longDescription: 'A pixel-perfect responsive website built to exact designer specifications. Demonstrates mastery of CSS flexbox, media queries, and responsive design principles.',
    tech: ['HTML', 'CSS', 'Flexbox', 'Responsive Design'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/fotomatic/',
    githubUrl: 'https://github.com/EddieJorden/fotomatic',
    image: '/fotomatic-thumbnail.png',
    features: [
      'Pixel-perfect implementation from design specs',
      'Fully responsive with CSS Flexbox',
      'Mobile-first approach',
      'Cross-browser compatibility',
    ],
    role: 'Frontend Developer',
    year: '2022',
  },
  {
    id: 8,
    slug: 'appointment-planner',
    title: 'Appointment Planner',
    description: 'Contact and appointment management system built with React. Features contact organization and scheduling.',
    longDescription: 'A contacts and appointments management tool with clean UX for scheduling and organizing daily tasks. Built with React for a smooth, component-driven user experience.',
    tech: ['React'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/react_challenge/',
    githubUrl: 'https://github.com/EddieJorden/react_challenge',
    image: '/appointment-planner-thumbnail.png',
    features: [
      'Contact management with CRUD',
      'Appointment scheduling',
      'Clean, intuitive UI',
      'React component architecture',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 9,
    slug: 'food-near-me',
    title: 'Food Near Me',
    description: 'Restaurant finder using the Yelp API. Search for restaurants by location with detailed information and reviews.',
    longDescription: 'A location-aware restaurant finder using the Yelp API. Users can search, filter, and discover restaurants near any location with detailed info and reviews.',
    tech: ['React', 'Yelp API'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/food.near.me/',
    githubUrl: 'https://github.com/EddieJorden/food.near.me',
    image: '/food_near_me_thumbnail.png',
    features: [
      'Yelp API integration',
      'Location-based restaurant search',
      'Filtering and sorting',
      'Detailed restaurant information',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 10,
    slug: 'my-jams',
    title: 'My Jams - Spotify Playlist Manager',
    description: 'Spotify playlist creator using the Spotify API. Create and manage playlists, search tracks, and add songs to existing playlists.',
    longDescription: 'A Spotify playlist manager using the Spotify API with OAuth authentication. Users can create playlists, search for tracks, and add songs to existing playlists.',
    tech: ['React', 'Spotify API', 'OAuth'],
    category: 'Frontend',
    liveUrl: 'https://eddiejorden.github.io/my-jams',
    githubUrl: 'https://github.com/EddieJorden/my-jams',
    image: '/myJams-thumbnail.png',
    features: [
      'Spotify API with OAuth authentication',
      'Playlist creation and management',
      'Track search and browsing',
      'Add tracks to existing playlists',
    ],
    role: 'Frontend Developer',
    year: '2023',
  },
  {
    id: 11,
    slug: 'developer-portfolio',
    title: 'Developer Portfolio',
    description: 'Custom-built responsive portfolio showcasing projects and skills. Built from scratch using React.js and Express.',
    longDescription: 'A custom-built responsive portfolio showcasing projects and skills, built from scratch with React.js for the frontend and Express for the backend.',
    tech: ['React', 'Express', 'Responsive Design'],
    category: 'Full-Stack',
    liveUrl: 'https://eddiemoger.com',
    githubUrl: 'https://github.com/EddieJorden/my-portfolio',
    image: '/Eddie-Moger-Developer-Portfolio.png',
    features: [
      'Custom React.js frontend',
      'Express backend',
      'Fully responsive design',
      'Project showcase with filtering',
    ],
    role: 'Full-Stack Developer',
    year: '2023',
  },
  {
    id: 12,
    slug: 'portfolio-racing',
    title: 'Modern Portfolio - Racing Theme',
    description: 'Dynamic racing-inspired portfolio with bold animations, unique UI/UX design, and modern aesthetics.',
    longDescription: 'A dynamic racing-inspired portfolio featuring bold animations, unique UI/UX design, and modern aesthetics. Built with glassmorphism effects, particle backgrounds, and smooth Framer Motion transitions.',
    tech: ['React', 'TypeScript', 'Framer Motion', 'Styled Components'],
    category: 'Frontend',
    liveUrl: 'https://eddie-portfolio-1k7tzzrm0-eddiejordens-projects.vercel.app/',
    githubUrl: 'https://github.com/EddieJorden/my-portfolio',
    image: '/portfolio-gradient.jpg',
    features: [
      'Racing-inspired visual theme',
      'Glassmorphism effects',
      'Particle background animations',
      'Smooth Framer Motion transitions',
    ],
    role: 'Frontend Developer',
    year: '2025',
  },
  {
    id: 13,
    slug: 'portfolio-premium',
    title: 'Modern Portfolio - Premium Dark',
    description: 'Sleek dark-themed portfolio with premium animations and professional design.',
    longDescription: 'A sleek dark-themed portfolio with premium animations and professional design. Demonstrates clean code architecture, modern React patterns, and polished UI craftsmanship.',
    tech: ['React', 'TypeScript', 'Framer Motion', 'Styled Components'],
    category: 'Frontend',
    liveUrl: 'https://eddie-portfolio-lwtoc5izw-eddiejordens-projects.vercel.app/',
    githubUrl: 'https://github.com/EddieJorden/my-portfolio',
    image: '/portfolio-premium.jpg',
    features: [
      'Premium dark theme',
      'Professional animations',
      'Clean code architecture',
      'Modern React patterns with TypeScript',
    ],
    role: 'Frontend Developer',
    year: '2025',
  },
  {
    id: 14,
    slug: 'portfolio-gradient',
    title: 'Modern Portfolio - Gradient Showcase',
    description: 'Vibrant gradient-based portfolio featuring Apple-inspired animations and modern design principles.',
    longDescription: 'A vibrant gradient-based portfolio featuring Apple-inspired animations and modern design principles. Built with multi-page architecture and SEO optimization using React Router.',
    tech: ['React', 'TypeScript', 'Framer Motion', 'React Router'],
    category: 'Frontend',
    liveUrl: 'https://eddie-portfolio-8picjqkwj-eddiejordens-projects.vercel.app/',
    githubUrl: 'https://github.com/EddieJorden/my-portfolio',
    image: '/portfolio-racing.jpg',
    features: [
      'Vibrant gradient visual design',
      'Apple-inspired animations',
      'Multi-page React Router architecture',
      'SEO optimization',
    ],
    role: 'Frontend Developer',
    year: '2025',
  },
];
